import { uniqueId } from 'underscore';
import $ from '../../../../../../FrontendBundle/Resources/js/jquery';
import AbstractGroupItem from '../../item/abstractGroupItem';
import ContainerItem from '../../item/containerItem';
import FoamItem from '../../item/foamItem';
import TabletopItem from '../../item/tabletopItem';
import CollisionError from '../problem/collisionError';

/**
 * @class CollisionView
 */
export default class CollisionView {
    paths = new Map();
    lastSelection = new Set();
    lastProblems = [];

    constructor(collisionChecker, layerManager, pathFactory, selection) {
        this.collisionChecker = collisionChecker;
        this.layerManager = layerManager;
        this.pathFactory = pathFactory;
        this.selection = selection;

        this.layer = this.layerManager.createItemsOverlayLayer('static-collisions', { fill: 'red', opacity: 0.5 });

        selection.on('change', (item) => {
            // Remove all remembered problems for changed items of the selection
            // That way old collisions won't be rerendered on selection change
            this._forgetProblemsByItem(item);
        });

        selection.on('selectionchange', () => {
            // First hide all paths for the currently selected items (leave that to the dynamic collisions view)
            const selectedItems = this._getSelectedItems();

            this._hidePathsForItems(selectedItems);

            // Now remember the current selection if one was made
            if (selectedItems.size > 0) {
                this._rememberSelection(selectedItems);
            }

            // Rerender all static collisions when selection is changed
            this.updateView(this.lastProblems);
        });
    }

    updateView(problems) {
        // Only handle collision errors. Also only display collision errors
        // that have at least two items (others can't be displayed)
        const collisionErrors = problems
            .filter(problem => problem instanceof CollisionError && problem.getItems().length >= 2);

        this._rememberProblems(collisionErrors);

        if (collisionErrors.length === 0) {
            this.cleanupView();
            return;
        }

        const oldGroup = this.group;
        this._createGroup();

        this.paths.clear();
        const selectedItems = this._getSelectedItems();

        collisionErrors.forEach((problem) => {
            let [item, otherItem] = problem.getItems();

            // Never render static collisions for items in the current selection. They will be handled by the dynamic
            // collision view
            if (selectedItems.has(item) || selectedItems.has(otherItem)) return;

            if (this._supportsBidirectionalCollisions(item, otherItem)) {
                this.setStaticCollisionClipping(otherItem, item);
            } else if (item.getSafetyMargin() === otherItem.getSafetyMargin() && this.lastSelection.has(otherItem)) {
                // Prefer the last selected item if they have the same safety margin
                [item, otherItem] = [otherItem, item];
            }

            this.setStaticCollisionClipping(item, otherItem);
        });

        // This allows for a more seamless experience as collisions won't disappear while they are recreated
        if (oldGroup) oldGroup.replaceWith(this.group);
        else this.layer.getElement().append(this.group);
    }

    cleanupView() {
        this.paths.clear();
        if (this.group) {
            this.group.remove();
            this.group = null;
        }
    }

    _hidePathsForItems(items) {
        items.forEach((item) => {
            if (this.paths.has(item)) this.paths.get(item).forEach(path => path.hide());
        });
    }

    _rememberSelection(selectedItems) {
        this.lastSelection.clear();
        selectedItems.forEach(item => this.lastSelection.add(item));
    }

    _rememberProblems(problems) {
        this.lastProblems = problems;
    }

    _forgetProblemsByItem(item) {
        const items = item instanceof AbstractGroupItem ? item.resolveGroupItems() : [item];
        this.lastProblems = this.lastProblems
            .filter(problem => !problem.getItems().find(subItem => items.indexOf(subItem) >= 0));
    }

    _getSelectedItems() {
        const items = this.selection
            .toArray()
            .map((item) => {
                if (item instanceof AbstractGroupItem) return item.resolveGroupItems();
                if (item instanceof ContainerItem) return [item].concat(item.getDescendants());
                return [item];
            })
            .reduce((flattened, subItems) => flattened.concat(subItems), []);

        return new Set(items);
    }

    _createGroup() {
        this.group = $(this.layer.group(document.createDocumentFragment()));
        this.group.addClass('static-collisions');
    }

    _supportsBidirectionalCollisions(...items) {
        // TODO: Remove specific tableleg handling...
        return !!items
            .filter(item => item.type !== 'tableleg')
            .find(item => item.supportsBidirectionalCollisions);
    }

    /**
     * Generates and appends static clipping paths
     *
     * @param {AbstractItem} item - leading collision Item
     * @param {AbstractItem} otherItem - leading collision Item
     */

    setStaticCollisionClipping(item, otherItem) {
        const svg = this.layer.getSvg();
        const id = uniqueId('static-collisions-clip-');

        const clipPath = svg.clipPath(this.group, id);
        const itemPath = svg.group(this.group, '', {
            'clip-path': `url(#${id})`,
            class: `item-${item.cid}`,
        });

        const marginPath = this.createMarginPath(itemPath, item)
            .addClass('selected')
            .data('item', item);

        const bagPath = this.createBagPath(clipPath, otherItem)
            .addClass('selected')
            .data('item', otherItem);

        if (!this.paths.has(item)) this.paths.set(item, []);
        if (!this.paths.has(otherItem)) this.paths.set(otherItem, []);

        this.paths.get(item).push(marginPath);
        this.paths.get(otherItem).push(bagPath);
    }

    createMarginPath(parent, item) {
        let path;

        if (item instanceof FoamItem || item instanceof TabletopItem) {
            path = this.pathFactory.createInnerCollisonPath(parent, item);
        } else {
            path = this.pathFactory.createMarginPath(parent, item);
        }

        path.setAttribute('transform', this.pathFactory.getAbsoluteTransformString(item, true));

        return $(path);
    }

    createBagPath(parent, item) {
        let path;

        if (item instanceof FoamItem || item instanceof TabletopItem) {
            path = this.pathFactory.createOuterCollisonPath(parent, item);
        } else {
            path = this.pathFactory.createBagPath(parent, item);
        }

        path.setAttribute('transform', this.pathFactory.getAbsoluteTransformString(item, true));

        return $(path);
    }
}
